import { Box, Checkbox, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import '@fontsource/rubik';
import { QUICK_TURN_UNAVAILABLE_MESSAGE } from '../../../utils/availability'

const QUICK_TURN_EXPLANATION = 'A Quick Turn is an expedited, 24-hour turnaround on your edited video. It\'s subject to editor availability and cannot be guaranteed.'

// Shared "Quick Turn" checkbox + disclaimer, used by both the create-project
// wizard (EditingStartDatePage.js, dark backdrop) and the edit-project drawer
// (CreateEvent.js, light background) so the two flows stay visually and
// behaviorally identical. `variant` controls only the color scheme.
//
// `disabled` (derived from the check_session `quick_turns.pending` count via
// `isQuickTurnUnavailable`) disables the checkbox and, on hover, explains why
// via `disabledReason` instead of the "what is a quick turn" explanation.
const QuickTurnCheckbox = ({ checked, onChange, variant = 'light', disabled = false, disabledReason = QUICK_TURN_UNAVAILABLE_MESSAGE, quotaText }) => {
    const isDark = variant === 'dark'

    const checkboxEl = (
        <Checkbox
            id="quick_turn"
            checked={Boolean(checked)}
            onChange={onChange}
            disabled={disabled}
            sx={{
                '& .MuiSvgIcon-root': { fontSize: isDark ? { xs: '25px', lg: '30px', xl: '34px' } : { xs: '16px', sm: '18px', lg: '20px', xl: '26px' }, margin: 0, padding: 0 },
                borderRadius: '5px',
                ...(isDark && !disabled ? { color: 'white !important' } : {}),
            }}
        />
    )

    return (
        <Box sx={{ width: isDark ? '80%' : '100%', mt: isDark ? 2 : 0, mb: isDark ? 0 : { xs: '25px', sm: '25px', md: '25px', lg: '25px', xl: '33px' } }}>
            <Box className='flex a-center' sx={{ ml: '-9px' }}>
                {disabled ? (
                    <Tooltip title={disabledReason} arrow placement="top">
                        <span data-testid="quick-turn-checkbox-disabled-wrapper">{checkboxEl}</span>
                    </Tooltip>
                ) : checkboxEl}
                <Typography sx={{ fontFamily: 'Rubik', fontSize: isDark ? { xs: '9px', sm: '11px', lg: '13px', xl: '16px' } : { xs: '9px', sm: '10px', md: '11px', lg: '12px', xl: '16px' }, color: isDark ? 'white' : 'inherit', fontWeight: isDark ? 400 : 600, opacity: disabled ? 0.5 : 1 }}>
                    Make this a 24 hour quick turn
                </Typography>
                <Tooltip title={QUICK_TURN_EXPLANATION} arrow placement="top">
                    <InfoOutlinedIcon
                        aria-label="What is a quick turn?"
                        sx={{ fontSize: isDark ? { xs: '13px', lg: '15px', xl: '18px' } : { xs: '11px', lg: '13px', xl: '16px' }, ml: 0.75, color: isDark ? 'white' : '#6b7470', cursor: 'help', opacity: disabled ? 0.5 : 1 }}
                    />
                </Tooltip>
            </Box>
            <Typography textAlign="left" sx={{ fontFamily: 'Rubik', fontSize: isDark ? { xs: '9px', sm: '10px', md: '11px', lg: '12px', xl: '16px' } : { xs: '7px', sm: '8px', md: '9px', lg: '10px', xl: '13px' }, color: isDark ? '#cfd6d3' : '#6b7470', mt: isDark ? 0.5 : 0.25 }}>
                Schedule at least 1 week in advance. Turnaround is based on business days.
            </Typography>
            {quotaText && (
                <Typography textAlign="left" sx={{ fontFamily: 'Rubik', fontSize: isDark ? { xs: '9px', sm: '10px', md: '11px', lg: '12px', xl: '16px' } : { xs: '7px', sm: '8px', md: '9px', lg: '10px', xl: '13px' }, color: isDark ? '#cfd6d3' : '#6b7470', mt: 0.25, fontWeight: 600 }}>
                    {quotaText}
                </Typography>
            )}
        </Box>
    )
}

export default QuickTurnCheckbox
